/**
 * Making the page behind an open dialog inert while a flow is running.
 *
 * 🔴 THE REPORTED FAILURE: ONE STRAY CLICK ENDS THE WALKTHROUGH. Every dialog
 * this app opens is a Radix `Dialog` (or the `Sheet`/`FloatingDrawer` built on
 * one), and Radix's `DismissableLayer` closes it on any pointerdown outside its
 * own node. So while the social-media flow had the Facebook composer open,
 * clicking anywhere on the page behind it — the sidebar, the list, the empty
 * space beside the panel — dismissed the composer. Reproduced in a real browser:
 * the dialog goes, `body` loses the `pointer-events: none` Radix put on it, the
 * flow is left standing on a step whose target no longer exists, and the rest of
 * its steps are recorded as skipped. The run ends having built nothing, on a
 * screen that is neither the composer nor a clean page.
 *
 * So for as long as a flow is running, pointer input that lands anywhere the
 * walkthrough does not own is swallowed here — the background stops being
 * clickable, and therefore stops being able to take the walkthrough apart.
 *
 * ┌─ WHAT IS DELIBERATELY STILL ALLOWED ──────────────────────────────────────┐
 * │ The flow's own chrome, anything Radix portals out of a dialog (a Select    │
 * │ list, a menu, a date popover, a toast — all siblings of the dialog in the  │
 * │ DOM, not descendants), the step's own highlighted target wherever it       │
 * │ happens to live, and — when one is open — the WHOLE of the host's modal.   │
 * │ That last one is the important carve-out: the user is filling in a real    │
 * │ form and still needs every control in it, not just the one under the ring. │
 * │ See `TaskSpotlight`'s own note on why the highlight has never been a       │
 * │ barrier.                                                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * 🔴 POINTER INPUT ONLY. Wheel, scroll and keyboard are untouched (see
 * `BLOCKED_EVENTS`), so a dimmed panel still scrolls and a focused field still
 * types — the shield stops the page being ACTIVATED, not being read.
 *
 * 🔴 AND IT COVERS THE WHOLE FLOW, NOT ONLY THE MODAL STEPS. It began as a
 * barrier around an open dialog only; `shouldShieldTarget` carries the full note
 * on why that was widened and what it costs.
 */

/** Anything portaled out of the dialog that must keep working. */
const PORTALED_LAYERS = [
  '[data-radix-popper-content-wrapper]',
  '[data-radix-select-content]',
  '[data-radix-menu-content]',
  '[data-radix-dropdown-menu-content]',
  '[data-radix-popover-content]',
  '[role="menu"]',
  '[role="listbox"]',
  '[role="tooltip"]',
  '[data-sonner-toaster]',
  '[data-radix-toast-viewport]',
  'li[role="status"]',
].join(',');

/** This module's own surfaces, plus the product tour's. */
const TOUR_CHROME = [
  '.tour-task-callout',
  '.tour-task-bar',
  '.tour-guide-tracker',
  '.tour-guide-actions',
  '.tour-guide-dock',
  '.driver-popover',
].join(',');

/** A host modal that is actually on screen. */
const OPEN_DIALOG = '[role="dialog"], [role="alertdialog"]';

/**
 * 🔴 THE TOUR'S OWN POPOVER IS A `role="dialog"` TOO, and counting it would turn
 * the guard on when nothing modal is open at all. driver.js renders
 * `.driver-popover` with that role (confirmed in a live run: a 340×250 element
 * with `role="dialog"` sitting on the page while the product tour is between
 * stops), and so does this module's demo-booking dialog. Reading either as "the
 * host has a modal open" would shield the whole page while the user is standing
 * on an ordinary page step, which is indistinguishable from the app having
 * frozen — the exact class of failure this module exists to remove.
 */
const OWN_DIALOGS = '.driver-popover, .tour-demo-dialog';

/**
 * The events a dismiss can start from.
 *
 * `pointerdown` is the one that matters — Radix dismisses on it, not on click —
 * but the whole press is swallowed so a blocked gesture cannot leave the page
 * with half of one (a `mouseup` with no `mousedown`, a focus move, a text
 * selection that started under the dim).
 */
const BLOCKED_EVENTS = [
  'pointerdown',
  'mousedown',
  'pointerup',
  'mouseup',
  'click',
  'dblclick',
  'contextmenu',
] as const;

/** Is this dialog on screen, rather than merely still in the DOM mid-close? */
function isOpen(dialog: Element): boolean {
  const state = dialog.getAttribute('data-state');
  if (state && state !== 'open') return false;
  const rect = dialog.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

/** Every HOST modal currently on screen — never this module's own. */
function openDialogs(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(OPEN_DIALOG)).filter(
    (dialog) => !dialog.closest(OWN_DIALOGS) && isOpen(dialog),
  );
}

/**
 * Should this event be swallowed?
 *
 * Exported for its own tests: it is the entire policy, and it is a pure function
 * of an element plus what is on screen.
 */
export function shouldShieldTarget(target: Element | null, current: Element | null): boolean {
  if (!target) return false;

  // Always live, whatever else is on screen: this module's own callout, bar and
  // tracker are how the user moves the flow along or leaves it. Blocking these
  // would trap someone inside a walkthrough, which is worse than anything the
  // shield is here to prevent.
  if (target.closest(TOUR_CHROME)) return false;
  // Anything Radix portals OUT of a dialog — a Select list, a menu, a date
  // popover, a toast. These are siblings of the dialog in the DOM, not
  // descendants, so a containment test alone would refuse them.
  if (target.closest(PORTALED_LAYERS)) return false;
  // The step's own target, wherever it lives — a flow may legitimately point at
  // something outside the dialog it has open (a tab behind it, a toolbar
  // control), and the whole point of the ring is that the thing under it can be
  // pressed. This is also the ONLY page element left pressable when no dialog is
  // open, which is what makes the shield below followable by hand.
  if (current && (current === target || current.contains(target))) return false;

  const dialogs = openDialogs();

  /**
   * 🔴 A MODAL OPEN MEANS THE WHOLE MODAL STAYS LIVE. The user is filling in a
   * real form and needs every control in it, not just the one under the ring —
   * see `TaskSpotlight`'s note on why the highlight has never been a barrier.
   * Only what is OUTSIDE the modal is swallowed.
   */
  if (dialogs.length > 0) return !dialogs.some((dialog) => dialog.contains(target));

  /**
   * 🔴 AND WITH NO MODAL OPEN, THE PAGE IS SHIELDED TOO — which is a REVERSAL of
   * what this module shipped with, so read this before narrowing it back.
   *
   * It began as a barrier around an open dialog only, on the reasoning that a
   * step pointing at a sidebar row or a page button is one the user is invited
   * to press themselves, and blocking the page for those would make the flow
   * impossible to follow by hand. The first half of that is still true and is
   * exactly why `current` is let through above. The second half was wrong: the
   * step's target IS the thing the user is invited to press, and it is the one
   * thing still pressable, so following along by hand works unchanged.
   *
   * What the narrow version left was the reported hole: on every step with no
   * dialog open — which is most of a flow's opening steps, and every step of a
   * walkthrough that never opens one — the dimmed page behind the spotlight was
   * fully live. A stray click there navigated the app out from under the
   * walkthrough, opened something the flow knew nothing about, or dismissed the
   * very thing the next step was about to point at. Reported as "the background
   * is clickable and disturbing the tour guide … it is making another
   * functionality other than the tour guide flow".
   *
   * Note this swallows POINTER input only (see `BLOCKED_EVENTS`) — wheel and
   * keyboard are untouched, so a dimmed panel still scrolls and a focused field
   * still types.
   */
  return true;
}

/**
 * Start shielding. Returns the teardown, which must be called when the flow
 * ends — a listener left on `document` would make the app feel broken long
 * after the walkthrough was over.
 *
 * `currentTarget` is read at EVENT TIME rather than captured, because the step
 * (and therefore the element that must stay pressable) changes underneath this.
 */
export function installBackdropGuard(currentTarget: () => Element | null): () => void {
  const onEvent = (event: Event) => {
    /**
     * 🔴 ONLY A REAL USER IS SHIELDED — THE FLOW DRIVES THE APP WITH THE SAME
     * EVENTS. This is not a refinement, it is what makes the shield usable at
     * all: every flow presses things on the user's behalf (`clickWhenReady`'s
     * `el.click()`, `pressElement`'s dispatched pointer sequence,
     * `openAccordion`), and almost none of those elements are the step's own
     * target at the moment they are pressed. Without this test the guard
     * swallowed the flow's own navigation — reproduced in a browser: starting
     * the social-media flow from `/home/dashboard` left the app sitting on the
     * dashboard, "never confirmed arrival on the social media workspace" in the
     * console, and the run stuck on step 1 forever.
     *
     * `isTrusted` is exactly the distinction wanted: true only for input the
     * browser itself generated. Anything this module dispatches is false.
     */
    if (!event.isTrusted) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!shouldShieldTarget(target, currentTarget())) return;

    // 🔴 ALL THREE, AND `stopImmediatePropagation` IS NOT REDUNDANT. Radix's
    // dismiss listener sits on `document` in the BUBBLE phase, which
    // `stopPropagation` from here (capture, on `document`) already prevents —
    // but `FloatingDrawer` adds its OWN capture-phase listener to `document`
    // for the same gesture, and listeners on the SAME node are only stopped by
    // the immediate form. `preventDefault` covers what the browser would have
    // done on its own (focus moves, text selection, a link followed).
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  for (const type of BLOCKED_EVENTS) {
    document.addEventListener(type, onEvent, true);
  }

  return () => {
    for (const type of BLOCKED_EVENTS) {
      document.removeEventListener(type, onEvent, true);
    }
  };
}
